import { GpsPoint, HalfEvent } from "refsix-js-models";
// ml-savitzky-golay ships CommonJS with a default export.
import savitzkyGolay from "ml-savitzky-golay";
import { GpsProcessingService } from "./GpsProcessingService";

// GPS filtering + smoothing pipeline (SCORZA-730, ported from REFSIX-775).
//
// Replaces the legacy percentile-threshold filter in the *stats* path with the
// research-backed pipeline validated against match video in the GPS smoothing
// playground:
//   accuracy gate → kinematic rejection → gap handling → Savitzky-Golay
// The window/polynomial below are the parameters agreed in the ticket after
// that review. Per-point speed is re-derived from the smoothed positions, so
// downstream sprint/speed stats no longer trust the device-reported speed.
//
// The legacy filter in GpsProcessingService is deliberately left in place: the
// display path (heatmap/sprint map via getFilteredFitnessData) and GPX import
// still use it. Only the stats path is smoothed.

// --- Agreed tuning (SCORZA-730 decision) ---------------------------------
export const SG_WINDOW = 7;
export const SG_POLY = 3;
// Accuracy gate: strict threshold, relaxed only if it would drop >5% of points.
export const ACCURACY_STRICT = 10; // metres
export const ACCURACY_PERMISSIVE = 15; // metres
export const ACCURACY_ESCALATION_DROP_RATE = 0.05;
// Kinematic rejection — MAX_SPEED is the reduced "bolt constant" for
// point-to-point rejection ("keep bolt constant or even reduce it").
export const MAX_SPEED = 10; // m/s
export const MAX_ACCEL = 6; // m/s²
export const MAX_JUMP = 12; // metres within ≤1.5s
// Gap handling: interpolate short dropouts, split the track on long ones so
// smoothing never blends across e.g. half time.
export const GAP_INTERP_MAX_SEC = 3;
export const GAP_SPLIT_SEC = 10;

export interface SmoothedGpsResult {
    geoPoints: GpsPoint[];
    accuracyThreshold: number;
}

const RAD = Math.PI / 180;
const EARTH_RADIUS = 6371008.8; // metres (mean)

// Equirectangular projection around a local reference — sub-metre accurate at
// pitch extents and exactly linear, which Savitzky-Golay assumes.
function project(
    lat: number,
    lon: number,
    latRef: number,
    lonRef: number
): { x: number; y: number } {
    const cosLat = Math.cos(latRef * RAD);
    return {
        x: (lon - lonRef) * RAD * cosLat * EARTH_RADIUS,
        y: (lat - latRef) * RAD * EARTH_RADIUS,
    };
}

function unproject(
    x: number,
    y: number,
    latRef: number,
    lonRef: number
): { lat: number; lon: number } {
    const cosLat = Math.cos(latRef * RAD);
    return {
        lat: latRef + y / EARTH_RADIUS / RAD,
        lon: lonRef + x / (EARTH_RADIUS * cosLat) / RAD,
    };
}

interface ProjectedPoint {
    x: number;
    y: number;
    t: number;
    accuracy: number;
}

// Least-squares slope of `values` per sample index — the rate Savitzky-Golay
// itself works in, since it fits over index rather than time.
function sampleSlope(values: number[]): number {
    const meanIndex = (values.length - 1) / 2;
    const mean = values.reduce((sum, v) => sum + v, 0) / values.length;
    let covariance = 0;
    let variance = 0;
    for (let i = 0; i < values.length; i++) {
        covariance += (i - meanIndex) * (values[i] - mean);
        variance += (i - meanIndex) * (i - meanIndex);
    }
    return covariance / variance;
}

/**
 * Savitzky-Golay over one axis of a segment, padded by linear extrapolation.
 *
 * The library's own "replicate" padding repeats the end coordinate, so the
 * cubic sees the referee standing still for (SG_WINDOW-1)/2 samples and then
 * setting off — a kink that is not in the data. The fit bends to meet it,
 * dragging the endpoint inward and pushing the next couple of points out, so
 * every segment edge reads low and then high. Extending the segment along its
 * own measured slope leaves a straight run straight, so constant-velocity
 * motion comes through unbiased.
 *
 * This matters beyond the ends of the track: GAP_SPLIT_SEC starts a new
 * segment at every long dropout, so a referee sprinting through one gets a
 * fabricated edge in the middle of real motion, and the bias there is upward.
 */
function smoothAxis(values: number[]): number[] {
    const step = (SG_WINDOW - 1) / 2;
    const headSlope = sampleSlope(values.slice(0, SG_WINDOW));
    const tailSlope = sampleSlope(values.slice(values.length - SG_WINDOW));
    const padded: number[] = [];
    for (let k = step; k >= 1; k--) {
        padded.push(values[0] - k * headSlope);
    }
    for (const value of values) {
        padded.push(value);
    }
    for (let k = 1; k <= step; k++) {
        padded.push(values[values.length - 1] + k * tailSlope);
    }
    // "none" consumes the padding we added, returning exactly values.length.
    return savitzkyGolay(padded, 1, {
        windowSize: SG_WINDOW,
        polynomial: SG_POLY,
        derivative: 0,
        pad: "none",
    });
}

/**
 * Runs the full filtering + smoothing pipeline over a raw GPS track.
 * Returns the smoothed during-play points (speed re-derived from position)
 * and the accuracy threshold the gate settled on. An unusable track (e.g.
 * every point worse than ACCURACY_PERMISSIVE) yields an empty geoPoints array.
 *
 * `acceptedAccuracyThreshold` is the threshold the data was already accepted
 * at upstream — a GPX import carries its own, typically looser than
 * ACCURACY_STRICT. Pass it and the gate uses it verbatim instead of running
 * the strict → permissive escalation: re-gating already-filtered data at a
 * stricter threshold silently discards points the import deliberately kept,
 * and can empty a previously-working track entirely.
 */
export function smoothGpsPoints(
    geoPoints: GpsPoint[],
    matchTimings: HalfEvent[],
    acceptedAccuracyThreshold?: number
): SmoothedGpsResult {
    // Validity + during-play checks are the service's — one source of truth.
    const gpsProcessingService = new GpsProcessingService();
    const duringPlay = gpsProcessingService
        .filterOutInvalidPoints(geoPoints)
        .filter((p) => typeof p.time === "number")
        .sort((a, b) => (a.time || 0) - (b.time || 0))
        .filter((p) =>
            gpsProcessingService._isPointDuringPlay(matchTimings, p.time)
        );

    // Accuracy gate: start strict, relax once if the strict gate is eating
    // more than ACCURACY_ESCALATION_DROP_RATE of the track. Data that arrives
    // with a threshold already applied is gated at that threshold and never
    // escalated — the upstream filter's decision stands.
    const acceptableAt = (threshold: number) =>
        duringPlay.filter(
            (p) => typeof p.accuracy !== "number" || p.accuracy <= threshold
        );
    const preAccepted = typeof acceptedAccuracyThreshold === "number";
    let accuracyThreshold = preAccepted
        ? (acceptedAccuracyThreshold as number)
        : ACCURACY_STRICT;
    let afterGate = acceptableAt(accuracyThreshold);
    if (
        !preAccepted &&
        duringPlay.length > 0 &&
        (duringPlay.length - afterGate.length) / duringPlay.length >
            ACCURACY_ESCALATION_DROP_RATE
    ) {
        accuracyThreshold = ACCURACY_PERMISSIVE;
        afterGate = acceptableAt(accuracyThreshold);
    }

    if (afterGate.length < 2) {
        return { geoPoints: [], accuracyThreshold };
    }

    // Project around the median position so one distant outlier can't drag
    // the reference.
    const lats = afterGate.map((p) => p.latitude).sort((a, b) => a - b);
    const lons = afterGate.map((p) => p.longitude).sort((a, b) => a - b);
    const latRef = lats[Math.floor(lats.length / 2)];
    const lonRef = lons[Math.floor(lons.length / 2)];

    const projected: ProjectedPoint[] = afterGate.map((p) => {
        const xy = project(p.latitude, p.longitude, latRef, lonRef);
        return {
            x: xy.x,
            y: xy.y,
            t: p.time as number,
            accuracy:
                typeof p.accuracy === "number" ? p.accuracy : accuracyThreshold,
        };
    });

    // Kinematic rejection: drop points implying impossible speed/accel/jumps.
    const accepted: ProjectedPoint[] = [];
    let prevSpeed = 0;
    for (const cur of projected) {
        if (accepted.length === 0) {
            accepted.push(cur);
            continue;
        }
        const prev = accepted[accepted.length - 1];
        const dt = (cur.t - prev.t) / 1000;
        if (dt <= 0) continue;
        const dx = cur.x - prev.x;
        const dy = cur.y - prev.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const speed = dist / dt;
        const accel = (speed - prevSpeed) / dt;
        const jumpBad = dist > MAX_JUMP && dt <= 1.5;
        if (speed > MAX_SPEED || Math.abs(accel) > MAX_ACCEL || jumpBad) {
            continue;
        }
        accepted.push(cur);
        prevSpeed = speed;
    }

    // Gap handling: interpolate gaps ≤ GAP_INTERP_MAX_SEC, keep medium gaps
    // as-is, and split the track on gaps > GAP_SPLIT_SEC (dropouts, half time)
    // so each continuous run is smoothed independently.
    const segments: ProjectedPoint[][] = [];
    let current: ProjectedPoint[] = [];
    for (const point of accepted) {
        if (current.length === 0) {
            current.push(point);
            continue;
        }
        const prev = current[current.length - 1];
        const dt = (point.t - prev.t) / 1000;
        if (dt <= GAP_INTERP_MAX_SEC) {
            // Round, not floor: real timestamps jitter, so a 2-sample dropout
            // arrives as dt ≈ 2.6-2.9s. Flooring backfills one point instead
            // of two and leaves a ~1.3s cadence where the segment should be
            // ~1Hz — and Savitzky-Golay fits over sample index, so uneven
            // spacing quietly widens the window in real time around dropouts.
            const steps = Math.round(dt);
            for (let k = 1; k < steps; k++) {
                const f = k / steps;
                current.push({
                    x: prev.x + f * (point.x - prev.x),
                    y: prev.y + f * (point.y - prev.y),
                    t: prev.t + Math.round(f * dt * 1000),
                    accuracy: Math.max(prev.accuracy, point.accuracy),
                });
            }
            current.push(point);
        } else if (dt <= GAP_SPLIT_SEC) {
            current.push(point);
        } else {
            segments.push(current);
            current = [point];
        }
    }
    if (current.length > 0) segments.push(current);

    // Savitzky-Golay smoothing per continuous segment. Segments shorter than
    // the window pass through unsmoothed.
    const smoothedSegments: ProjectedPoint[][] = segments.map((segment) => {
        if (segment.length < SG_WINDOW) {
            return segment;
        }
        const xsSmooth = smoothAxis(segment.map((p) => p.x));
        const ysSmooth = smoothAxis(segment.map((p) => p.y));
        return segment.map((p, i) => ({
            x: xsSmooth[i] != null ? xsSmooth[i] : p.x,
            y: ysSmooth[i] != null ? ysSmooth[i] : p.y,
            t: p.t,
            accuracy: p.accuracy,
        }));
    });

    const smoothedGeoPoints: GpsPoint[] = [];
    for (const segment of smoothedSegments) {
        smoothedGeoPoints.push(...toGeoPoints(segment, latRef, lonRef));
    }
    return { geoPoints: smoothedGeoPoints, accuracyThreshold };
}

// Unproject a smoothed projected segment back to GpsPoints, re-deriving each
// point's speed from consecutive smoothed positions. Called per continuous
// segment so the first point of a segment never gets a speed derived across
// the gap (dropout / half time) that caused the split.
function toGeoPoints(
    smoothed: ProjectedPoint[],
    latRef: number,
    lonRef: number
): GpsPoint[] {
    const geoPoints: GpsPoint[] = [];
    for (let i = 0; i < smoothed.length; i++) {
        const ll = unproject(smoothed[i].x, smoothed[i].y, latRef, lonRef);
        let speed = 0;
        if (i > 0) {
            const dt = (smoothed[i].t - smoothed[i - 1].t) / 1000;
            if (dt > 0) {
                const dx = smoothed[i].x - smoothed[i - 1].x;
                const dy = smoothed[i].y - smoothed[i - 1].y;
                speed = Math.sqrt(dx * dx + dy * dy) / dt;
            }
        }
        geoPoints.push({
            latitude: ll.lat,
            longitude: ll.lon,
            time: smoothed[i].t,
            accuracy: smoothed[i].accuracy,
            speed,
        });
    }
    return geoPoints;
}
